import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";

/** Comment composer surface with the accepted reply-tail corner and public/internal paint. */
export function CommentComposerSurface({
  children,
  presentation = "resident",
}: {
  children: ReactNode;
  presentation?: "resident" | "public" | "internal";
}): ReactNode {
  return (
    <div
      className={twJoin(
        "rounded-lg rounded-tl-none",
        presentation === "resident" && "p-space-sm",
        presentation !== "resident" && "relative border p-space-md",
        presentation === "public" && "border-border-brand",
        presentation === "internal" && "border-border-warning-secondary bg-background-warning-secondary",
      )}
    >
      {children}
    </div>
  );
}

/** Responsive run of optional actions displayed by the ticket composer. */
export function CommentComposerActions({ children }: { children: ReactNode }): ReactNode {
  return <div className="flex flex-wrap items-center gap-space-sm @xs:w-full">{children}</div>;
}

type CommentType = "public" | "internal";

export function CommentComposerTypeSelector({
  allowedTypes,
  disabled,
  internalIcon,
  internalLabel,
  onSelect,
  publicLabel,
  selectedType,
}: {
  allowedTypes: CommentType[];
  disabled?: boolean;
  internalIcon: ReactNode;
  internalLabel: string;
  onSelect: (type: CommentType) => void;
  publicLabel: string;
  selectedType: CommentType;
}): ReactNode {
  if (allowedTypes.length === 0) {
    return null;
  }

  const hasPublic = allowedTypes.includes("public");
  const hasInternal = allowedTypes.includes("internal");

  return (
    <Inline wrap={false}>
      {hasPublic && (
        <Button
          data-testid="public-comment-btn"
          variant={selectedType === "public" ? "filled" : "soft"}
          size="sm"
          shape={hasInternal ? "start" : "default"}
          onClick={() => onSelect("public")}
          disabled={disabled}
        >
          {publicLabel}
        </Button>
      )}
      {hasInternal && (
        <Button
          data-testid="internal-note-btn"
          variant={selectedType === "internal" ? "filled" : "soft"}
          tone="warning"
          size="sm"
          shape={hasPublic ? "end" : "default"}
          icon={internalIcon}
          onClick={() => onSelect("internal")}
          disabled={disabled}
        >
          {internalLabel}
        </Button>
      )}
    </Inline>
  );
}
